import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FormProps } from "@rjsf/core";
import { useState } from "react";

import { TrashIcon } from "@nens/ranalib";
import { SchemaForFormData } from "@nens/shared-utils";

import { BrostarForm, BrostarUiSchema } from "@/libs/brostar-form";

interface DeleteFormData {
  projectNumber: number | undefined;
  correctionReason: string;
}

const getSchema = (type: string): SchemaForFormData<DeleteFormData> => ({
  type: "object",
  required: ["projectNumber", "correctionReason"],
  description: `Weet je zeker dat je deze ${type} wil verwijderen?`,
  properties: {
    projectNumber: {
      type: "number",
      title: "Projectnummer",
    },
    correctionReason: {
      type: "string",
      title: "Correctie reden",
      enum: ["eigenCorrectie", "inOnderzoek"],
      default: "eigenCorrectie",
    },
  },
});

const uiSchema: BrostarUiSchema<DeleteFormData> = {
  projectNumber: {},
  correctionReason: {
    "ui:enumNames": ["Eigen correctie", "In onderzoek"],
  },
};

type DeleteButtonProps = {
  type: string;
  onDelete: (projectNumber: string, correctionReason: string) => Promise<void>;
};

export const DeleteButton = ({ type, onDelete }: DeleteButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const toast = useToast();

  const onSubmit: FormProps<DeleteFormData>["onSubmit"] = async ({ formData }) => {
    if (!formData) return;
    setIsLoading(true);
    try {
      await onDelete(String(formData.projectNumber), formData.correctionReason);
      toast({
        title: "Success",
        description: "Verwijder bericht verstuurd",
        status: "success",
        duration: 9000,
        isClosable: true,
      });
      onClose();
    } catch (e) {
      toast({
        title: "Error",
        description: "Er is iets fout gegaan tijdens het verwijderen",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
    setIsLoading(false);
  };

  return (
    <>
      <Button variant="outlineGrey" onClick={onOpen} ml="at" isLoading={isLoading}>
        <TrashIcon />
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay backdropFilter="blur(5px)" bg={`rgba(255, 255, 255, 0.6)`} />
        <ModalContent
          minW="500px"
          w="fit-content"
          maxW="calc(100% - 40px)"
          h="fit-content"
          maxH="calc(100% - 40px)"
        >
          <ModalHeader px={10} pb={0} pt={10} display="flex" alignItems="center">
            <h2>Verwijder</h2>
          </ModalHeader>
          <ModalBody overflow="auto" py={2} px={10}>
            <BrostarForm
              schema={getSchema(type)}
              uiSchema={uiSchema}
              onSubmit={onSubmit}
              uploadTask={undefined}
              renderButtons={({ submitButton }) => <Flex ml="auto">{submitButton}</Flex>}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
